// Dynamically loads the PayPal JS SDK (standard Buttons) once per client id.
// Mirrors the analytics script-injection pattern (no extra npm dependency), so it
// works in the Emergent CRA build without a bundler plugin.

let loadPromise = null;
let loadedClientId = null;

export function loadPayPalSdk(clientId, { currency = 'USD', intent = 'capture' } = {}) {
  if (!clientId) {
    return Promise.reject(new Error('Missing PayPal client id'));
  }

  // Already loaded for this client id.
  if (typeof window !== 'undefined' && window.paypal && loadedClientId === clientId) {
    return Promise.resolve(window.paypal);
  }

  // A load is already in flight for this client id.
  if (loadPromise && loadedClientId === clientId) {
    return loadPromise;
  }

  loadedClientId = clientId;
  loadPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('PayPal SDK can only load in the browser'));
      return;
    }

    // Remove any prior SDK script so a client-id/currency change re-initializes cleanly.
    const existing = document.querySelector('script[data-tfd-paypal-sdk="true"]');
    if (existing) {
      existing.remove();
      if (window.paypal) {
        try { delete window.paypal; } catch (_) { window.paypal = undefined; }
      }
    }

    const params = new URLSearchParams({
      'client-id': clientId,
      currency,
      intent,
      components: 'buttons',
    });

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
    script.async = true;
    script.setAttribute('data-tfd-paypal-sdk', 'true');
    script.onload = () => {
      if (window.paypal) {
        resolve(window.paypal);
      } else {
        reject(new Error('PayPal SDK loaded but window.paypal is undefined'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load the PayPal SDK'));
    document.head.appendChild(script);
  });

  return loadPromise;
}
